import {common, priority} from '@priority-software/priority-proto-api/lib';
import {observable, action, makeObservable} from 'mobx';
import {Screen} from './Screen';
import {Form} from './Form';
import {FormsCacheData} from './FormsCache';
import {findFormByLevel} from './utils';
import {FormattedKeyboardEvent} from '../../features/Macro/utils/macroEventFormats';
import {MacroItem} from '../../features/Macro/types';
import {systemLang} from '../../Localization/languages';

export class FormsStore {
    screens: Map<string, Screen> = new Map();
    cachedData = new FormsCacheData();
    sysConstants: {[k: string]: common.IConstant};
    systemLangs: systemLang[] = [];
    htmlSignature: priority.netitems.form.IHtmlSignatureBase = null;

    recordingMacro: Partial<MacroItem> = null;

    @observable macros: MacroItem[] = [];
    @observable showMacroButtons: boolean = false;
    @observable isRecording: boolean = false;
    @observable currentSession: string;
    @observable onBackground: boolean;
    @observable newFormSidePanelSubforms: {[key: string]: string};
    @observable legacyFormSidePanelSubforms: {[key: string]: string};
    @observable designPanelOpened: boolean;

    constructor() {
        makeObservable(this);
    }

    get sidePanelSubforms() {
        return this.newFormSidePanelSubforms || this.legacyFormSidePanelSubforms;
    }

    @action
    setIsRecording = (isRecording: boolean) => {
        this.isRecording = isRecording;
    };

    @action
    setShowMacroButtons = (show: boolean) => {
        this.showMacroButtons = show;
    };

    setRecordingMacro = ({title, shortCode, isRecursive = false}: Partial<MacroItem>) => {
        this.recordingMacro = {
            title: title,
            eventArray: [],
            shortCode: shortCode,
            isRecursive: isRecursive
        };
    };

    setEventForRecordingMacro = (event: FormattedKeyboardEvent) => {
        if (this.isRecording && this.recordingMacro) {
            this.recordingMacro.eventArray.push(event);
        }
    };

    removeRecordingMacro = () => {
        this.recordingMacro = null;
    };

    setMacros(macros: MacroItem[]) {
        this.macros = macros.slice().sort((a, b) => a.title.localeCompare(b.title));
    }

    addMacro(macro: MacroItem) {
        const sameNameMacroIndex = this.macros?.findIndex(item => item.title === macro.title);
        if (sameNameMacroIndex === -1) {
            this.macros.push(macro);
            this.macros = this.macros.slice().sort((a, b) => a.title.localeCompare(b.title));
        } else {
            this.macros[sameNameMacroIndex] = {
                ...macro,
                id: this.macros[sameNameMacroIndex].id
            };
        }
    }

    removeMacro(macroID: string) {
        const macros = this.macros.filter(macro => macro.id !== macroID);
        this.setMacros(macros);
    }

    addForm = (key: string, value: Screen) => {
        this.screens.set(key, value);
    };

    deleteForm = (session: string) => {
        this.screens.delete(session);
        this.cachedData.clearSessionCache(session);
    };

    get currentScreen(): Screen {
        return this.screens?.get(this.currentSession);
    }

    get currentForm(): Form {
        return this.currentScreen?.form;
    }

    @action
    setCurrentSession = (session: string) => {
        this.currentSession = session;
    };

    @action
    updateSidePanelSubformsState = (rowIndex: number) => {
        if (this.currentScreen.getActiveForm().searchModeLevel > 0) {
            return;
        }
        const row = this.currentScreen.getActiveForm().records[rowIndex];
        if (row) {
            const nextDisabled = row.isEmpty() ? '1' : '0';
            if (this.newFormSidePanelSubforms?.isDisabled !== nextDisabled) {
                this.newFormSidePanelSubforms = {
                    ...this.newFormSidePanelSubforms,
                    ...{isDisabled: nextDisabled}
                };
            }
        }
    };

    getScreenBySession = (session: string): Screen => {
        return this.screens?.get(session);
    };

    getFormBySession = (session: string): Form => {
        return this.screens?.get(session)?.form;
    };

    getActiveForm = (): Form => {
        return this.currentScreen?.getActiveForm();
    };

    getFormByLevel = (session: string, level: number) => {
        const form = this.getFormBySession(session);
        if (form) {
            return findFormByLevel(form, level);
        }
        return null;
    };

    setSystemConstants = (constants: {[k: string]: common.IConstant}): void => {
        this.sysConstants = constants;
    };

    @action
    setNewSession = (session: string) => {
        this.onBackground = false;
        this.currentSession = session;
    };

    @action
    setDesignPanelOpen = () => {
        this.designPanelOpened = true;
    };
}

export const formsStore = new FormsStore();
